import { Link } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { CreditCard, List, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccountSuspension } from '@/hooks/useAccountSuspension';
import { useSuspensionSummary } from '@/hooks/useSuspensionSummary';
import { usePermission } from '@/hooks/usePermission';
import { parseTimestamp } from '@/utils/datetime';
import { config } from '@/config';
import { ContinueAsAnotherAccount } from '@/components/auth/ContinueAsAnotherAccount';

/**
 * What a suspended account sees in place of the application.
 *
 * This replaces the app rather than overlaying it, which is the whole point:
 * anything still readable behind a banner is a reason to put off paying. What
 * survives is the scale of what has stopped — counts, not inventory. A number
 * says how much monitoring is off; it can't help anyone limp along without it.
 *
 * The two exits are paying and signing out.
 */
export function AccountSuspendedScreen() {
  const { reason } = useAccountSuspension();
  const { data: summary } = useSuspensionSummary();
  const canManageBilling = usePermission('billing:read');

  const stoppedAt = parseTimestamp(summary?.suspendedAt);
  const failedAt = parseTimestamp(summary?.lastPaymentFailedAt);
  const devices = summary?.deviceCount ?? 0;
  const gateways = summary?.gatewayCount ?? 0;
  const hasScale = devices > 0 || gateways > 0;

  // The store's reason comes from whichever request tripped the lockout; the
  // summary's is authoritative once loaded. Either beats a generic message.
  const explanation =
    summary?.reason ??
    reason ??
    'Your account has been suspended. Update your payment method to restore access.';

  return (
    <main className="min-h-svh flex flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="flex flex-col items-center mb-6">
          <div className="w-10 h-10 rounded-md bg-sev-1 flex items-center justify-center text-white mb-3">
            <ShieldAlert className="w-5 h-5" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Account suspended</h1>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm p-6 space-y-5">
          {hasScale ? (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {stoppedAt
                  ? `${config.appName} stopped monitoring ${formatDistanceToNow(stoppedAt, { addSuffix: true })}:`
                  : `${config.appName} is no longer monitoring:`}
              </p>
              <div className="flex items-center justify-center gap-8 mt-3">
                <div>
                  <div className="text-3xl font-semibold tabular-nums">{devices}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">
                    {devices === 1 ? 'device' : 'devices'}
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold tabular-nums">{gateways}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">
                    {gateways === 1 ? 'gateway' : 'gateways'}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <p className="text-sm text-center">{explanation}</p>

          {failedAt ? (
            <p className="text-xs text-muted-foreground text-center">
              Last payment attempt failed {formatDistanceToNow(failedAt, { addSuffix: true })}.
            </p>
          ) : null}

          {canManageBilling ? (
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button asChild className="flex-1">
                <Link to="/admin/billing">
                  <CreditCard className="w-4 h-4" />
                  Update payment method
                </Link>
              </Button>
              <Button asChild variant="outline" className="flex-1">
                <Link to="/admin/plans">
                  <List className="w-4 h-4" />
                  View plans
                </Link>
              </Button>
            </div>
          ) : (
            // Offering a control that bounces off `billing:read` would read as
            // a broken button; say who can actually fix it instead.
            <p className="text-sm text-center text-muted-foreground pt-1">
              Ask an administrator on your account to update the payment method.
            </p>
          )}
        </div>

        {/* Suspension is per organization, so another account this browser holds may be fine
            (docs/MultiAccountSessions.md §9). This screen replaces the whole shell, menu
            included, so the switch is offered here. */}
        <div className="mt-4">
          <ContinueAsAnotherAccount to="/home" />
        </div>

        <p className="text-center mt-4">
          <Link
            to="/signout"
            className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            Sign out
          </Link>
        </p>
      </div>
    </main>
  );
}
